'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import {
  X,
  Handshake,
  Bot,
  Store,
  ExternalLink,
  ChevronRight,
  ArrowRight,
  Globe,
  TrendingUp,
  Users,
  Sparkles,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { BuddifyLogo } from '@/components/ui/buddify-logo'
import { CartNavLink } from '@/components/community/cart/cart-nav-link'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { productHeaderConfig, productStats } from '@/lib/constants'
import { usePathname } from 'next/navigation'
import {
  StartFreeLauncherContent,
  StartFreeTrigger,
} from './start-free-launcher'

const products = [
  {
    name: 'Brand Partnerships',
    tagline: 'Expand Your Business',
    description: 'Connect with partner stores for dropshipping, wholesale, and marketplace sales',
    href: '/brand-partnerships',
    icon: Handshake,
    color: 'primary' as const,
    stats: `${productStats.brandPartnerships.brands} brands`,
    statsIcon: Globe,
  },
  {
    name: 'Buddy AI',
    tagline: 'Convert More Visitors',
    description: 'AI-powered discounts, exit intent, lead capture, and smart product Q&A',
    href: '/buddy-ai-assistant',
    icon: Bot,
    badge: 'Built for Shopify',
    color: 'secondary' as const,
    stats: `${productStats.aiAssistant.conversionIncrease} more conversions`,
    statsIcon: TrendingUp,
  },
  {
    name: 'Marketplace',
    tagline: 'Sell Direct to Customers',
    description: 'List your products on the Buddify marketplace and reach new customers',
    href: '/marketplace',
    icon: Store,
    color: 'tertiary' as const,
    stats: `${productStats.marketplace.products} products`,
    statsIcon: Users,
  },
]

const mainNav = [
  { name: 'Partners', href: '/partners' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Contact', href: '/contact' },
  { name: 'Help Center', href: 'https://support.buddify.app', external: true },
]

interface MobileNavProps {
  open: boolean
  onClose: () => void
}

export function MobileNav({ open, onClose }: MobileNavProps) {
  const [startFreeExpanded, setStartFreeExpanded] = useState(false)
  const prefersReducedMotion = useReducedMotion()
  const pathname = usePathname()

  const activeProduct =
    productHeaderConfig.find(
      (product) =>
        pathname === product.href || pathname.startsWith(`${product.href}/`)
    ) ?? null

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/'
    return pathname === href || pathname.startsWith(href + '/')
  }

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: prefersReducedMotion ? 1 : 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: prefersReducedMotion ? 1 : 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
            className="fixed inset-0 bg-black/40 z-[90] lg:hidden"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            id="mobile-nav"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
            initial={{ x: prefersReducedMotion ? 0 : '100%' }}
            animate={{ x: 0 }}
            exit={{ x: prefersReducedMotion ? 0 : '100%' }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : { type: 'spring', damping: 28, stiffness: 280 }
            }
            className="fixed top-0 right-0 w-full max-w-sm h-dvh bg-panel shadow-xl z-[100] lg:hidden"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-line">
                <BuddifyLogo href={undefined} />
                <div className="flex items-center gap-1">
                  <span onClick={onClose}>
                    <CartNavLink className="p-2 min-w-11 justify-center text-muted hover:text-ink transition-colors" />
                  </span>
                  <button
                    type="button"
                    onClick={onClose}
                    className="p-2 -mr-2 min-w-11 min-h-11 inline-flex items-center justify-center text-muted hover:text-ink transition-colors"
                    aria-label="Close menu"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* Our Products */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-muted uppercase tracking-wider mb-3">
                    Our Products
                  </p>
                  <div className="space-y-3">
                    {products.map((product, index) => (
                      <motion.div
                        key={product.name}
                        initial={{ opacity: prefersReducedMotion ? 1 : 0, x: prefersReducedMotion ? 0 : 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: prefersReducedMotion ? 0 : index * 0.05 }}
                      >
                        <Link
                          href={product.href}
                          onClick={onClose}
                          className={cn(
                            'block p-4 rounded-xl border transition-all duration-200',
                            'active:scale-[0.98]',
                            product.color === 'primary' &&
                              'border-brand-green-line bg-brand-green-soft/30',
                            product.color === 'secondary' &&
                              'border-brand-violet-line bg-brand-violet-soft/30',
                            product.color === 'tertiary' &&
                              'border-brand-blue-line bg-brand-blue-soft/30'
                          )}
                        >
                          <div className="flex items-start gap-3">
                            {/* Icon */}
                            <div
                              className={cn(
                                'shrink-0 p-2.5 rounded-xl',
                                product.color === 'primary' &&
                                  'bg-brand-green-soft text-brand-green',
                                product.color === 'secondary' &&
                                  'bg-brand-violet-soft text-brand-violet',
                                product.color === 'tertiary' &&
                                  'bg-brand-blue-soft text-brand-blue'
                              )}
                            >
                              <product.icon className="w-5 h-5" />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="font-heading font-semibold text-ink">
                                  {product.name}
                                </span>
                                {product.badge && (
                                  <Badge
                                    variant="secondary"
                                    className="text-[10px] py-0 px-1.5"
                                  >
                                    <Sparkles className="w-2.5 h-2.5 mr-0.5" />
                                    {product.badge}
                                  </Badge>
                                )}
                              </div>
                              <p
                                className={cn(
                                  'text-xs font-medium mb-1',
                                  product.color === 'primary' && 'text-brand-green',
                                  product.color === 'secondary' && 'text-brand-violet',
                                  product.color === 'tertiary' && 'text-brand-blue'
                                )}
                              >
                                {product.tagline}
                              </p>
                              <p className="text-sm text-muted line-clamp-2 mb-2">
                                {product.description}
                              </p>

                              {/* Stats & Arrow */}
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted font-medium flex items-center gap-1">
                                  {product.statsIcon && (
                                    <product.statsIcon className="w-3 h-3" />
                                  )}
                                  {product.stats}
                                </span>
                                <ArrowRight className="w-4 h-4 text-muted" />
                              </div>
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Product-level links */}
                {activeProduct && (
                  <div className="mb-6">
                    <Link
                      href={activeProduct.href}
                      onClick={onClose}
                      className={cn(
                        'inline-block text-sm font-semibold uppercase tracking-wider mb-3 hover:opacity-80 transition-opacity',
                        activeProduct.theme === 'dark' ? 'text-brand-violet' : 'text-brand-green'
                      )}
                    >
                      {activeProduct.name}
                    </Link>
                    <div className="space-y-1">
                      {activeProduct.links.map((link) => (
                        <Link
                          key={link.name}
                          href={link.href}
                          onClick={onClose}
                          className={cn(
                            'flex items-center justify-between p-3 rounded-xl transition-colors',
                            isActive(link.href)
                              ? activeProduct.theme === 'dark'
                                ? 'bg-brand-violet-soft text-brand-violet'
                                : 'bg-brand-green-soft text-brand-green'
                              : 'hover:bg-page text-ink'
                          )}
                        >
                          <span className="font-medium">{link.name}</span>
                          <ChevronRight className="w-5 h-5 text-muted" />
                        </Link>
                      ))}
                      <a
                        href={activeProduct.cta.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={onClose}
                        className={cn(
                          'flex items-center justify-between p-3 rounded-xl transition-colors',
                          activeProduct.theme === 'dark'
                            ? 'bg-ai-action text-ai-on-action active:opacity-90'
                            : 'bg-primary-600 text-white active:bg-primary-700'
                        )}
                      >
                        <span className="font-medium">
                          {activeProduct.cta.label}
                        </span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                )}

                {/* Main Nav */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-muted uppercase tracking-wider mb-3">
                    Resources
                  </p>
                  <div className="space-y-1">
                    <Link
                      href="/community"
                      onClick={onClose}
                      className={cn(
                        'flex items-center justify-between p-3 rounded-xl transition-colors',
                        isActive('/community')
                          ? 'bg-brand-green-soft text-brand-green'
                          : 'hover:bg-page active:bg-raised text-ink'
                      )}
                    >
                      <span className="font-medium flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Community
                      </span>
                      <ChevronRight className="w-5 h-5 text-muted" />
                    </Link>

                    {mainNav.map((item, index) => (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: prefersReducedMotion ? 1 : 0, x: prefersReducedMotion ? 0 : 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: prefersReducedMotion ? 0 : 0.15 + index * 0.05 }}
                      >
                        {item.external ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onClose}
                            className="flex items-center justify-between p-3 rounded-xl hover:bg-page active:bg-raised transition-colors"
                          >
                            <span className="font-medium text-ink flex items-center gap-2">
                              {item.name}
                              <ExternalLink className="w-4 h-4 text-muted" />
                            </span>
                            <ChevronRight className="w-5 h-5 text-muted" />
                          </a>
                        ) : (
                          <Link
                            href={item.href}
                            onClick={onClose}
                            className="flex items-center justify-between p-3 rounded-xl hover:bg-page active:bg-raised transition-colors"
                          >
                            <span className="font-medium text-ink">
                              {item.name}
                            </span>
                            <ChevronRight className="w-5 h-5 text-muted" />
                          </Link>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="p-4 border-t border-line bg-page/50">
                <StartFreeTrigger
                  controlsId="start-free-launcher-mobile"
                  expanded={startFreeExpanded}
                  onClick={() => setStartFreeExpanded(!startFreeExpanded)}
                  className="w-full justify-between"
                />

                {/* Expandable Apps List */}
                <AnimatePresence>
                  {startFreeExpanded && (
                    <motion.div
                      id="start-free-launcher-mobile"
                      initial={{ height: prefersReducedMotion ? 'auto' : 0, opacity: prefersReducedMotion ? 1 : 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: prefersReducedMotion ? 'auto' : 0, opacity: prefersReducedMotion ? 1 : 0 }}
                      transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-3">
                        <StartFreeLauncherContent onNavigate={onClose} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
